import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Sparkles, Mail } from "lucide-react";

const formSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  websiteUrl: z
    .string()
    .min(4, "Please enter your website URL")
    .refine(
      (val) =>
        /^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}(\/.*)?$/.test(val),
      { message: "Please enter a valid website URL" }
    ),
  additionalInfo: z.string().optional(),
  email: z.string().email("Please enter a valid email address"),
});

type VPGFormValues = z.infer<typeof formSchema>;

const VPGHeroSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<VPGFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      websiteUrl: "",
      additionalInfo: "",
      email: "",
    },
  });

  const onSubmit = async (data: VPGFormValues) => {
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://app.upfixe.com/api/public/generate-value-proposition",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyName: data.companyName,
            websiteUrl: data.websiteUrl,
            additionalInfo: data.additionalInfo || "",
            email: data.email,
          }),
        }
      );

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Your value proposition is on its way!");
      } else if (response.status === 429) {
        toast.error("You've reached the limit. Please try again in an hour.");
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting VP form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="pt-32 pb-24 px-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background to-primary/5" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/3 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge className="px-4 py-1.5 text-sm bg-primary/10 text-primary border-primary/20 mb-6">
                Free AI-Powered Tool
              </Badge>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Free Value Proposition Generator{" "}
              <span className="text-primary">for Google Ads</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              We'll scan your website, understand what you do, and craft a
              custom value proposition for your Google Ads — delivered straight
              to your inbox.
            </motion.p>

            {/* Sample output preview */}
            <motion.div
              className="bg-white rounded-xl border border-border p-6 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Sample Output
              </p>
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-4 border-l-4 border-l-indigo-500 border border-slate-200">
                  <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
                    Elevator Pitch
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Acme Plumbing provides 24/7 emergency plumbing with
                    fixed-price repairs and no call-out fees, giving Dallas
                    homeowners peace of mind when pipes burst at 2am.
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-l-green-500 border border-green-200">
                  <p className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">
                    Unique Selling Proposition
                  </p>
                  <p className="text-sm text-green-800 font-medium">
                    The only Dallas plumber offering fixed-price quotes before
                    arrival — no surprises, no hidden fees, guaranteed.
                  </p>
                </div>
                <div className="bg-pink-50 rounded-lg p-4 border-l-4 border-l-pink-500 border border-pink-200">
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-1">
                    Taglines
                  </p>
                  <p className="text-sm text-pink-800 font-semibold italic">
                    "Fixed Price. Fast Response. No Surprises."
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="shadow-2xl border-2 border-primary/20">
              <CardContent className="p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.1 }}
                    >
                      <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-3">
                      Check Your Inbox!
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      We're scanning your website and crafting your custom value
                      proposition. It'll land in your inbox shortly.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>Didn't get it? Check your spam folder.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-6">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <h2 className="text-xl font-bold">
                        Generate Yours Today
                      </h2>
                    </div>
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                      >
                        <FormField
                          control={form.control}
                          name="companyName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Acme Plumbing"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="websiteUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Website URL</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="acmeplumbing.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="additionalInfo"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Anything else we should know?{" "}
                                <span className="text-muted-foreground font-normal">
                                  (optional)
                                </span>
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="E.g. we serve the Dallas area, focus on emergency calls, 15 years in business..."
                                  className="min-h-20 resize-none"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="you@company.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <Button
                          type="submit"
                          className="w-full h-12 text-lg"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <div className="flex items-center justify-center">
                              <div className="h-4 w-4 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2" />
                              Scanning your site...
                            </div>
                          ) : (
                            <>
                              Generate My Value Proposition{" "}
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      </form>
                    </Form>
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      Free forever. No credit card. No spam.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VPGHeroSection;
